import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import AlertMessages from "../common/AlertMessages";
import { withLDConsumer } from "launchdarkly-react-client-sdk";

// NOTE: We are not using the `Tabs` from CX, as it does not provide needed
// functionality in how tab changes are handled.
import Tabs from "../Tabs";

import Tab from "@cx/ui/Tab";
import ProfilePage from "../profile";
import AttachedSolutions from "../attachedSolutions";
import AccountSecurity from "../accountSecurity";
import NotificationSettings from "../notificationSettings";
import MergeSummaryPage from "./MergeSummaryPage";
import AccountMergeModal from "./AccountMergeModal";
import LoadingIndicatorCentered from "../reusableComponents/LoadingIndicatorCentered";
import LoadingIndicator from "@cx/ui/LoadingIndicator";
import { displayPasswordChangeMessage } from "../../utils/changePassword";
import { hideAlertMessage } from "../../actions/statusActions";
import "./_homePage.scss";
import ToastMessage from "../common/ToastMessage";
import bridgeBar from "../../utils/bridgeBar";
import {
  passwordChangeQueryParam,
  mergeSuccessQueryParam
} from "../../utils/stringConstants";
import getParameterByName from "../../utils/urlUtil";
import appConfig from "../../config/appConfig";
import { Trans } from "react-i18next";
import i18n from "i18next";
import { attachSolution } from "../../actions/solutionActions";
import {
  fetchMergePreview,
  performAccountMerge,
  cancelAccountMerge,
  fetchSwappedEligibility,
  resetSwap,
  displayMergeSuccessMessage
} from "../../actions/accountMergeActions";
import { reportToGTM } from "../../api/gtm-analytics";

class HomePage extends React.Component {
  constructor(props) {
    super(props);

    if (!this.props.user.isRegistered) {
      this.state = {
        tab: "attached-solutions",
        showMergeModal: false
      };
    } else {
      this.state = {
        tab: "profile",
        showMergeModal: false
      };
    }
  }

  componentDidMount() {
    bridgeBar.renderBridgeBar(i18n.language);
    // The internal logic is determined here with the solution links, but this will be modified in the future by setting up a bff to get membership and look for a CAI entity.
    let internal = false;
    for (const solution of this.props.user.solutionLinks) {
      if (solution.solutionId.startsWith("TP_COXAUTO")) {
        internal = true;
        break;
      }
    }
    if (this.props.ldClient.variation("pdt.inject-pendo")) {
      window.pendo?.initialize({
        visitor: {
          id: this.props.user.id,
          username: this.props.user.profile.preferredUsername,
          full_name: this.props.user.profile.preferredUsername,
          first_name: this.props.user.profile.givenName,
          last_name: this.props.user.profile.familyName,
          internal,

          // Common Core Specific User Context
          cc_environment: appConfig.auth.env,
          cc_partition: appConfig.partition,
          product: "coco_My_Settings"
        },
        account: {
          id: "coco_my_settings_" + appConfig.auth.env
        }
      });
    }

    const currentLocation = window.location.href;
    if (currentLocation.includes(passwordChangeQueryParam)) {
      this.props.displayPasswordChangeMessage();
      // remove password change flag from browser URL without refreshing
      // TODO: Do this in a separate function and call the function here
      const url = new URL(currentLocation);
      const searchParams = url.searchParams;
      searchParams.delete(passwordChangeQueryParam);
      url.search = searchParams.toString();
      window.history.replaceState(
        "replaceStateData",
        "replaceStateTitle",
        url.toString()
      );
    }

    if (currentLocation.includes(mergeSuccessQueryParam)) {
      this.props.displayMergeSuccessMessage();
      // remove merge success flag from browser URL without refreshing
      const url = new URL(currentLocation);
      const searchParams = url.searchParams;
      searchParams.delete(mergeSuccessQueryParam);
      url.search = searchParams.toString();
      window.history.replaceState(
        "replaceStateData",
        "replaceStateTitle",
        url.toString()
      );
    }

    if (window.location.pathname.indexOf("/solutionauthcallback") >= 0) {
      const state = JSON.parse(
        atob(getParameterByName("state", window.location.href))
      );

      const authCode = getParameterByName("code", window.location.href);

      window.history.replaceState(
        "replaceStateData",
        "replaceStateTitle",
        "/?partition=" + appConfig.partition
      );

      /* eslint-disable react/no-did-mount-set-state */
      this.setState({
        tab: "attached-solutions"
      });
      /* eslint-enable react/no-did-mount-set-state */

      const isUserNotLegacy =
        this.props.user.identity.passwordPolicy !== "LEGACY";

      this.props.attachSolution(
        {
          solution: state.authenticatedSolution,
          redirectUrl: "https://" + window.location.hostname,
          authorizationCode: authCode
        },
        isUserNotLegacy
      );
    } else if (window.location.pathname.indexOf("/accountmergecallback") >= 0) {
      // Handle account merge OAuth callback
      const authCode = getParameterByName("code", window.location.href);
      const returnedState = getParameterByName("state", window.location.href);

      // Clean up the URL
      window.history.replaceState(
        "replaceStateData",
        "replaceStateTitle",
        "/?partition=" + appConfig.partition
      );

      // Validate the state parameter to prevent CSRF attacks
      let isValidState = false;
      if (returnedState) {
        try {
          const stateData = JSON.parse(atob(returnedState));
          // Verify this is an account-merge operation and not too old (5 minutes max)
          const MAX_STATE_AGE_MS = 5 * 60 * 1000;
          isValidState =
            stateData.operation === "account-merge" &&
            stateData.initiatedAt &&
            Date.now() - stateData.initiatedAt < MAX_STATE_AGE_MS;
        } catch (e) {
          // Invalid state - could be tampered or corrupted
          console.error("Invalid OAuth state parameter:", e);
          isValidState = false;
        }
      }

      if (authCode && isValidState) {
        // Dispatch Redux action to fetch merge preview
        this.props.fetchMergePreview(authCode);
      } else if (authCode && !isValidState) {
        // Auth code present but state is invalid - potential CSRF or expired session
        console.warn(
          "Account merge callback received with invalid or expired state"
        );
        // Still attempt the merge - the BFF will validate the auth code
        // This allows recovery from edge cases like page refresh during OAuth flow
        this.props.fetchMergePreview(authCode);
      }
    }

    this.setStateBasedOnTab();
  }

  /**
   * Initiates the account merge OAuth flow by redirecting to the SignIn
   * /bridge-identity endpoint. The user will authenticate with their second
   * Bridge account, and SignIn will redirect back to /accountmergecallback
   * with an auth code containing the second account's identity.
   */
  initiateAccountMerge() {
    // Build state object to pass through the OAuth flow
    const state = btoa(
      JSON.stringify({
        operation: "account-merge",
        initiatedAt: Date.now()
      })
    );

    // Generate a random nonce for the OAuth request
    const nonce =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);

    // Build the OAuth redirect URL
    // Note: No solutionid parameter - Bridge IDP is auto-detected by SignIn
    const redirectUrl =
      appConfig.signin.bridgeIdentityURL +
      "&nonce=" +
      nonce +
      "&state=" +
      state +
      "&redirect_uri=" +
      encodeURIComponent(appConfig.signin.accountMergeRedirectURL) +
      "&abortUrl=" +
      encodeURIComponent(appConfig.signin.abortURL) +
      "&client_id=" +
      appConfig.signin.accountMergeClientId;

    reportToGTM(
      "Merge Bridge Accounts Button",
      "Clicked",
      "Initiate Account Merge OAuth Flow",
      "Attached Solutions"
    );

    // Redirect to SignIn for second account authentication
    window.location.assign(redirectUrl);
  }

  setStateBasedOnTab() {
    if (window.location.hash.includes("#attached-solutions")) {
      /* eslint-disable react/no-did-mount-set-state */
      this.setState({
        tab: "attached-solutions"
      });
      /* eslint-enable react/no-did-mount-set-state */
      this.replaceUrlWithPartitionOnly();
    }

    if (window.location.hash.includes("#account-security")) {
      /* eslint-disable react/no-did-mount-set-state */
      this.setState({
        tab: "account-security"
      });
      /* eslint-enable react/no-did-mount-set-state */
      this.replaceUrlWithPartitionOnly();
    }

    if (window.location.hash.includes("#notifications")) {
      /* eslint-disable react/no-did-mount-set-state */
      this.setState({
        tab: "notifications"
      });

      /* eslint-enable react/no-did-mount-set-state */
      this.replaceUrlWithPartitionOnly();
    }

    if (window.location.pathname.includes("authcallback")) {
      this.replaceUrlWithPartitionOnly();
    }
  }

  replaceUrlWithPartitionOnly() {
    if (window.location.href.includes("partition")) {
      if (appConfig.partition === "non-prod") {
        const cleanedUrl = "/?partition=" + appConfig.partition;
        window.history.replaceState("", "My Settings", cleanedUrl);
      } else {
        window.history.replaceState("", "My Settings", "/");
      }
    } else {
      window.history.replaceState("", "My Settings", "/");
    }
  }

  render() {
    const { user, alertMessages, hideAlertMessage } = this.props;
    const isTabActive = (val) => {
      return this.state.tab === val;
    };

    const onActiveTabChanged = (key, index) => {
      let newTab;
      if (key.includes("profileTab")) {
        newTab = "profile";
      } else if (key.includes("securityTab")) {
        newTab = "account-security";
      } else if (key.includes("solutionsTab")) {
        newTab = "attached-solutions";
      } else if (key.includes("notificationsTab")) {
        newTab = "notifications";
      }
      this.setState(
        {
          tab: newTab
        },
        () => {
          window.location.hash = "#" + this.state.tab;
        }
      );
      reportToGTM(
        `${newTab} tab`,
        "Clicked",
        `Switched to ${newTab} tab`,
        "User settings home page"
      );
    };

    // Show merge summary page if triggered (only when we have real data from BFF)
    const { accountMerge } = this.props;

    // Show loading state while fetching merge preview (but NOT for swap eligibility - that's handled inline)
    if (accountMerge.isLoadingPreview) {
      return (
        <div className="home-page-wrapper">
          <LoadingIndicatorCentered
            htmlId="merge-preview-loading"
            size="large"
          />
        </div>
      );
    }

    // Show loading indicator while polling for merge propagation
    if (accountMerge.isVerifying) {
      return (
        <div className="home-page-wrapper">
          <div className="merge-verification-container">
            <LoadingIndicator
              htmlId="merge-verification-loading"
              size="large"
            />
            <p className="merge-verification-text">
              {i18n.t(
                "merge.verification.loading",
                "Verifying merge completion, this may take up to 2 minutes..."
              )}
            </p>
          </div>
        </div>
      );
    }

    // Merge completed — redirect is imminent. Show empty wrapper to prevent
    // a flash of the normal home page between the last verification dispatch
    // (which clears isVerifying) and the window.location redirect.
    if (accountMerge.mergeCompleted) {
      return <div className="home-page-wrapper" />;
    }

    if (accountMerge.showMergePage && accountMerge.previewData) {
      const mergeData = accountMerge.previewData;

      return (
        <>
          {accountMerge.error && (
            <div className="merge-error-message">
              <AlertMessages
                alertMessages={[
                  {
                    id: "merge-error",
                    type: "danger",
                    text: accountMerge.error
                  }
                ]}
                hideAlertMessage={() => {}}
              />
            </div>
          )}
          <MergeSummaryPage
            secondAccount={mergeData.secondAccount}
            primaryAccount={mergeData.primaryAccount}
            combinedAccount={mergeData.combinedAccount}
            onSubmit={(swapAccounts) =>
              this.props.performAccountMerge(swapAccounts)
            }
            onCancel={() => this.props.cancelAccountMerge()}
            onSwap={() => this.props.fetchSwappedEligibility()}
            onResetSwap={() => this.props.resetSwap()}
            isSubmitting={accountMerge.isExecutingMerge}
            isLoadingSwap={accountMerge.isLoadingSwap}
            isSwapped={accountMerge.isSwapped}
            isEligible={mergeData.isEligible}
            conflicts={mergeData.conflicts}
            swappedEligibility={accountMerge.swappedEligibility}
            expiresAt={mergeData.expiresAt}
          />
        </>
      );
    }

    // Note: mergeError is handled via toast message in the callback handler
    // The normal home page renders below with ToastMessage component

    return (
      <div className="home-page-wrapper">
        <ToastMessage />
        <React.Fragment>
          {/*
            This PageHeader is block referenced as part of our smoke test running in New Relic.  If we change anything
            inside this block, just be aware that the smoke test will very likely also need to be updated to match!
          */}
          <div className={"page-header"}>
            <h1>
              <Trans i18nKey="common.my-settings" /> - {user.profile.givenName}{" "}
              {user.profile.familyName} ({user.profile.preferredUsername})
            </h1>
          </div>
          {alertMessages.length !== 0 && (
            <AlertMessages
              alertMessages={alertMessages}
              hideAlertMessage={hideAlertMessage}
            />
          )}
          {!this.props.user.isRegistered ? (
            <Tabs htmlId="TabsDefault" onActiveTabChanged={onActiveTabChanged}>
              <Tab
                htmlId="solutionsTab"
                label={<Trans i18nKey="solution-tab.header" />}
                active={isTabActive("attached-solutions")}
              >
                <AttachedSolutions
                  onMergeClick={() => this.setState({ showMergeModal: true })}
                />
              </Tab>
              {this.props.ldClient.variation(
                "pdt.show-notifications-tab",
                false
              ) && (
                <Tab
                  htmlId="notificationsTab"
                  label={<Trans i18nKey="notifications-tab.header" />}
                  active={isTabActive("notifications")}
                >
                  <NotificationSettings />
                </Tab>
              )}
            </Tabs>
          ) : (
            <Tabs htmlId="TabsDefault" onActiveTabChanged={onActiveTabChanged}>
              <Tab
                htmlId="profileTab"
                label={<Trans i18nKey="profile-tab.header" />}
                active={isTabActive("profile")}
              >
                <ProfilePage />
              </Tab>
              <Tab
                htmlId="securityTab"
                label={<Trans i18nKey="security-tab.header" />}
                active={isTabActive("account-security")}
              >
                <AccountSecurity />
              </Tab>
              <Tab
                htmlId="solutionsTab"
                label={<Trans i18nKey="solution-tab.header" />}
                active={isTabActive("attached-solutions")}
              >
                <AttachedSolutions
                  onMergeClick={() => this.setState({ showMergeModal: true })}
                />
              </Tab>
              {this.props.ldClient.variation(
                "pdt.show-notifications-tab",
                false
              ) && (
                <Tab
                  htmlId="notificationsTab"
                  label={<Trans i18nKey="notifications-tab.header" />}
                  active={isTabActive("notifications")}
                >
                  <NotificationSettings />
                </Tab>
              )}
            </Tabs>
          )}
          <AccountMergeModal
            show={this.state.showMergeModal}
            onCancel={() => this.setState({ showMergeModal: false })}
            onContinue={() => {
              this.setState({ showMergeModal: false });
              this.initiateAccountMerge();
            }}
          />
        </React.Fragment>
      </div>
    );
  }
}

HomePage.propTypes = {
  accountMerge: PropTypes.shape({
    isLoadingPreview: PropTypes.bool,
    isLoadingSwap: PropTypes.bool,
    isExecutingMerge: PropTypes.bool,
    isVerifying: PropTypes.bool,
    previewData: PropTypes.object,
    authCode: PropTypes.string,
    error: PropTypes.string,
    showMergePage: PropTypes.bool,
    mergeCompleted: PropTypes.bool,
    isSwapped: PropTypes.bool,
    swappedEligibility: PropTypes.object
  }).isRequired,
  alertMessages: PropTypes.array.isRequired,
  attachSolution: PropTypes.func.isRequired,
  cancelAccountMerge: PropTypes.func.isRequired,
  displayMergeSuccessMessage: PropTypes.func.isRequired,
  displayPasswordChangeMessage: PropTypes.func.isRequired,
  fetchMergePreview: PropTypes.func.isRequired,
  fetchSwappedEligibility: PropTypes.func.isRequired,
  flags: PropTypes.object,
  hideAlertMessage: PropTypes.func.isRequired,
  ldClient: PropTypes.shape({
    variation: PropTypes.func.isRequired
  }).isRequired,
  performAccountMerge: PropTypes.func.isRequired,
  resetSwap: PropTypes.func.isRequired,
  user: PropTypes.object
};

function mapStateToProps(state) {
  return {
    user: state.userReducer.user,
    alertMessages: state.statusReducer.alertMessages,
    accountMerge: state.accountMergeReducer
  };
}

const mapDispatchToProps = {
  displayPasswordChangeMessage,
  hideAlertMessage,
  attachSolution,
  fetchMergePreview,
  performAccountMerge,
  cancelAccountMerge,
  fetchSwappedEligibility,
  resetSwap,
  displayMergeSuccessMessage
};

const connectedHomePage = connect(
  mapStateToProps,
  mapDispatchToProps
)(HomePage);

export default withLDConsumer({})(connectedHomePage);
